<template>
  <gmap-map
    id="map"
    ref="map"
    :center="mapCenter"
    :zoom="mapZoom"
    :options="mapOptions"
    @bounds_changed="onBoundsChanged"
  >
    <gmap-marker
      :key="i"
      v-for="(s, i) in monoBrandStores"
      :position="s.coords"
      :icon="constructIcon('brand', [24, 36])"
      @click="onMarkerClick(c)"
    ></gmap-marker>
    <gmap-marker
      :key="i"
      v-for="(c, i) in clusters"
      :position="c.coords"
      :label="{text: ((c.properties || {}).point_count || ' ').toString(), fontSize: '11px'}"
      :icon="constructIcon('retailer', [24, 36])"
      @click="onMarkerClick(c)"
    ></gmap-marker>
    <gmap-marker
      :key="position"
      v-if="currentPosition"
      :position="{lat: currentPosition.latitude, lng: currentPosition.longitude}"
      :icon="constructIcon('location', [16, 16])"
    ></gmap-marker>
  </gmap-map>
</template>

<script>
  import { EventBus } from '@/bus'
  import googleMapsStyling from '@/assets/map/style.json'
  import geohash from 'ngeohash'
  import cc from 'i18n-iso-countries'
  import supercluster from 'supercluster'

  export default {
    name: 'GoogleMap',
    props: ['lang'],
    data: () => ({
      bounds: null,
      currentZoom: null,
      cluster: null,
      clusters: [],
      monoBrandStores: [],
      currentlySeenGeohashes: [],
      mapCenter: {lat: 48, lng: 18},
      mapOptions: {
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: 'greedy',
        styles: googleMapsStyling
      },
      mapZoom: 5,
      marker: null,
      maxZoomOut: 6,
      place: null,
      position: null,
      request: null,
      stores: [],
      timer: null
    }),
    computed: {
      currentPosition () {
        return this.$store.state.currentPosition
      },
      map () {
        return this.$refs.map
      },
      selectedMarker () {
        return this.$store.state.selectedMarker
      }
    },
    methods: {
      constructIcon (type, scaled) {
        return {
          url: `/static/images/types/${type}.png`.toLowerCase(),
          scaledSize: new google.maps.Size(...scaled),
          labelOrigin: new google.maps.Point(12, 13)
        }
      },
      createCluster () {
        this.cluster = supercluster({
          radius: 160,
          maxZoom: 16
        })
        this.monoBrandStores = this.stores.filter(s => s.monobrand === '1' || s.monobrand === true).map(store => ({
          coords: {
            lat: parseFloat(store.latitude),
            lng: parseFloat(store.longitude)
          },
          properties: {...store}
        }))
        const places = this.stores.filter(s => !s.monobrand || s.monobrand !== '1').map(store => {
          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [store.longitude, store.latitude]
            },
            properties: {...store}
          }
        })

        try {
          this.cluster.load(places)
        } catch (e) {
          console.debug('Failed to create cluster', e)
        }
      },
      getMarkers () {
        const { south, west, north, east } = this.bounds.toJSON()
        this.clusters = this.cluster.getClusters([west, south, east, north], this.currentZoom).map(cluster => {
          cluster.coords = {
            lng: parseFloat(cluster.geometry.coordinates[0]),
            lat: parseFloat(cluster.geometry.coordinates[1])
          }
          return cluster
        })
      },
      fetchStores () {
        return new Promise((resolve, reject) => {
          const { south, west, north, east } = this.bounds.toJSON()
          const zoom = this.map.$mapObject.zoom

          if (zoom < this.maxZoomOut) {
            this.currentlySeenGeohashes = []
            this.stores = []
            return resolve()
          }

          const additionalGeohashes = geohash.bboxes(south, west, north, east, 3)
            .filter(hash => !(this.currentlySeenGeohashes.indexOf(hash) > -1))

          this.currentZoom = zoom

          /* Cancel last request if a new is made */
          const CancelToken = this.$http.CancelToken
          if (this.request !== null) this.request.cancel()
          this.request = CancelToken.source()

          if (additionalGeohashes.length === 0) return resolve()

          this.$http.all(additionalGeohashes.map(geohash => {
            return this.$http.get(`/stores/geohash/${geohash}`, {
              cancelToken: this.request.token
            })
          }))
            .then(response => {
              this.currentlySeenGeohashes = [...this.currentlySeenGeohashes, ...additionalGeohashes]

              const items = response.reduce((prev, curr) => prev.concat(curr.data), [])
                .filter(item =>
                  (!item.closingDate || Date.now() < item.closingDate) &&
                  (!item.openingDate || Date.now() > item.openingDate)
                )

              this.stores = [...this.stores, ...items]
              return resolve()
            })
            .catch(() => {
              // Silent fail
            })
        })
      },
      hasAcceptedPosition () {
        return new Promise((resolve, reject) => {
          if (!navigator.permissions) {
            return resolve(false)
          }
          navigator.permissions.query({name: 'geolocation'})
            .then(permission => resolve((permission.state === 'granted')))
        })
      },
      tryToFetchCurrentPosition () {
        EventBus.$emit('getCurrentPosition')
      },
      tryToFetchCountryPosition () {
        return new Promise((resolve, reject) => {
          if (!this.lang) return resolve(false)

          const redirects = {
            sa_ar: 'sa'
          }

          const overrides = {
            ir: 'iran',
            uk: 'england'
          }

          const address = overrides[this.lang] || cc.getName(redirects[this.lang] || this.lang, 'en')

          if (!address) return resolve(false)

          new google.maps.Geocoder().geocode({address}, (results, status) => {
            if (status !== 'OK') return resolve(false)

            this.place = results[0]
            return resolve(this.place.geometry.viewport)
          })
        })
      },
      initializeCenter () {
        this.hasAcceptedPosition()
          .then(hasAccepted => {
            if (hasAccepted) this.tryToFetchCurrentPosition()

            this.tryToFetchCountryPosition()
              .then(viewport => {
                if (viewport && !(this.currentPosition !== null && typeof this.currentPosition === 'object')) {
                  this.map.fitBounds(viewport)
                  this.$store.commit('initPosition', viewport)
                }

                if (!hasAccepted) {
                  this.$store.commit('loading', false)
                }
              })
          })
      },
      onBoundsChanged (bounds) {
        this.bounds = bounds
        this.$store.commit('bounds', bounds)

        if (this.selectedMarker) {
          const { latitude, longitude } = this.selectedMarker
          const latLng = new google.maps.LatLng(latitude, longitude)

          if (!this.bounds.contains(latLng)) {
            this.$store.commit('selectedMarker', null)
          }
        }

        clearTimeout(this.timer)
        this.timer = setTimeout(() => {
          this.mapZoom = this.map.$mapObject.zoom
          this.mapCenter = this.map.$mapObject.center

          this.fetchStores()
            .then(this.createCluster)
            .then(this.getMarkers)
            .then(this.setList)

          if (!this.currentPosition) return

          const currentLatLng = new google.maps.LatLng(this.currentPosition.latitude, this.currentPosition.longitude)
          const isWithin = this.bounds.contains(currentLatLng)
          if (isWithin !== this.positionWithinBounds) {
            this.positionWithinBounds = isWithin
            this.$store.commit('positionWithinBounds', this.bounds.contains(currentLatLng))
          }
        }, 60)
      },
      onMarkerClick (marker) {
        const latLng = new google.maps.LatLng(marker.geometry.coordinates[1], marker.geometry.coordinates[0])

        if ((marker.properties || {}).cluster_id) { // If cluster
          this.mapCenter = latLng
          this.mapZoom = this.cluster.getClusterExpansionZoom(marker.properties.cluster_id)
        } else { // If marker
          this.map.panTo(latLng)
          this.$store.commit('selectedMarker', marker.properties)
        }
      },
      setList () {
        EventBus.$emit('markers', this.stores)
      },
      centerMarker (marker) {
        if (!marker) return

        const latLng = new google.maps.LatLng(marker.latitude, marker.longitude)
        this.map.panTo(latLng)
        if (this.map.$mapObject.zoom < 15) this.mapZoom = 15
      },
      recenterViewport () {
        if (this.$store.state.initPosition) {
          return this.map.fitBounds(this.$store.state.initPosition)
        }
      },
      updateMapOnAutoComplete (place) {
        if (!place.geometry) return
        this.place = place
        this.map.fitBounds(place.geometry.viewport)
      }
    },
    watch: {
      selectedMarker (marker) {
        this.centerMarker(marker)
      }
    },
    mounted () {
      EventBus.$on('autocomplete', this.updateMapOnAutoComplete)
      EventBus.$on('recenterViewport', this.recenterViewport)
      EventBus.$on('recenterPosition', () => {
        this.centerMarker(this.currentPosition)
      })

      this.map.$mapCreated.then(() => {
        this.initializeCenter()
        EventBus.$emit('googleReady')
      })
    }
  }
</script>

<style scoped>
  #map {
    position: absolute !important;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }
  @media only screen and (max-device-width: 768px) {
    #map {
      height: 70vh;
      position: relative !important;
    }
  }
  @media only screen and (max-device-width: 736px) {
    #map {
      height: 50vh;
      position: relative !important;
    }
  }
</style>



// WEBPACK FOOTER //
// Map.vue?99138a92